import React, {useState, useEffect, useRef} from 'react';
import {
  Button,
  Switch,
  Grid,
  CircularProgress
} from '@material-ui/core';
import axios from 'axios';
import { makeStyles } from '@material-ui/core/styles';
import SplitPane from 'react-split-pane';
import Divider from '@material-ui/core/Divider';

import CodeViewer from './CodeViewer';
import utils from '../../libs/utils';

interface Directory {
  name: string
  path: string
  children: Directory[]
}

interface FileExplorerProps {
  directory: Directory[]
  isNested?: boolean
  onSelect: (a: Directory) => void
  rootUrl: string
  activeSrc?: string
}

interface ZipFileExplorerProps {
  fileId: string
  query?: {[key: string]: string}
  getDirectoryListUrl?: string
  directoryRootUrl?: string
}

const useStyles = makeStyles((theme) => ({
  nestedDirectory: {
    paddingLeft: '8px',
    marginLeft: '12px',
    borderLeft: '1px dashed rgba(0,0,0,0.2)',
  },
  directoryItem: {
    display: 'block',
    position: 'relative',
    padding: '3px 7px',
    borderRadius: '4px',

    '&.clickable': {
      color: theme.palette.primary.dark,
      textDecoration: 'none',

      '&:hover': {
        textDecoration: 'none',
        backgroundColor: 'rgba(0,0,0,0.05)'
      },

      '&.active': {
        color: theme.palette.primary.contrastText,
        fontWeight: '400',
        backgroundColor: theme.palette.primary.main
      }
    },

    '&.nested:after': {
      content: '""',
      position: 'absolute',
      borderBottom: '1px dashed rgba(0,0,0,0.2)',
      height: '0px',
      width: '8px',
      top: '50%',
      right: '100%'
    }
  },
  panel: {
    overflow: 'auto',
    height: '100%',
    width: '100%'
  },
  iframe: {
    width: '100%',
    height: '99%',
    border: 'none'
  }
}));

const FileExplorer = (props: FileExplorerProps) => {
  const {
    directory,
    isNested,
    onSelect,
    rootUrl,
    activeSrc
  } = props;
  const classes = useStyles();

  const isItemActive = (item: Directory): boolean => {
    if (!item.children.length) {
      return (activeSrc || '').toLowerCase().indexOf(item.path.toLowerCase()) >= 0;
    }
    return false;
  };

  const getItemClass = (item: Directory): string => {
    const result: string[] = [classes.directoryItem];

    if (isNested)
      result.push('nested');

    if (!item.children.length)
      result.push('clickable');

    if (isItemActive(item))
      result.push('active');

    return result.join(' ');
  };

  return (
    <>
      {directory.map((item) => (
        <div className={isNested ? classes.nestedDirectory : ''} key={item.path}>
          {(() => {
            if (item.children.length)
              return (
                <>
                  <div className={getItemClass(item)}>
                    {item.name}
                  </div>
                  {!!item.children && !!item.children.length && (
                    <FileExplorer activeSrc={activeSrc} rootUrl={rootUrl} onSelect={onSelect} directory={item.children} isNested={true} />
                  )}
                </>
              );

            return (
              <a href={`${rootUrl}/${item.path}`}
                 className={getItemClass(item)}
                 onClick={e => {
                   e.preventDefault();
                   onSelect(item);
                 }}>
                {item.name}
              </a>
            );
          })()}
        </div>
      ))}
    </>
  );
};

export const ZipFileExplorer = (props: ZipFileExplorerProps) => {
  const {
    fileId,
    getDirectoryListUrl = '/classroom/zip-file/directory/access',
    directoryRootUrl = `${axios.defaults.baseURL}/classroom/zip-file/directory/browser/${fileId}`,
    query = {}
  } = props;
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [directory, setDirectory] = useState<Directory[]>([]);
  const [activeSrc, setActiveSrc] = useState<string>('');
  const [showTextOnly, setShowTextOnly] = useState<boolean>(false);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const classes = useStyles();

  const getIndexDirectory = (directory: Directory[]): Directory | null => {
    for (let item of directory) {
      if (/^index\./i.test(item.name))
        return item;
    }

    if (directory.length === 1 && directory[0].children.length > 0)
      return getIndexDirectory(directory[0].children);

    return null;
  };

  const loadDirectory = async () => {
    try {
      setIsLoading(true);
      setErrorMessage('');
      setDirectory([]);
      setActiveSrc('');

      if (fileId) {
        const { data } = await axios.post(getDirectoryListUrl, {
          fileId: fileId
        }, {
          params: query,
          withCredentials: true
        });

        const rootList: Directory[] = [];
        const insertToList = (list: Directory[], fileName: string, parentDirectory?: Directory) => {
          const parts = fileName.split('/').filter(a => a.trim());
          const name = parts.splice(0, 1)[0];
          const instance = list.filter(a => a.name === name)[0];

          if (instance) parentDirectory = instance;
          else list[list.length] = parentDirectory = {
            name: name,
            path: parentDirectory ? [parentDirectory.path, name].join('/') : name,
            children: []
          };

          if (parts.length) insertToList(parentDirectory.children, parts.join('/'), parentDirectory);
        };

        for (let fileName of data.files as string[]) {
          insertToList(rootList, fileName);
        }
        setDirectory(rootList);

        setTimeout(() => {
          const indexDirectory = getIndexDirectory(rootList);
          if (indexDirectory)
            handleSelect(indexDirectory);
        });
      }
    }
    catch (e) {
      setErrorMessage(utils.getRequestErrorMessage(e));
    }
    finally {
      setIsLoading(false);
    }
  };

  const handleSelect = (directory: Directory) => {
    const src = `${directoryRootUrl}/${directory.path}`;
    if (iframeRef.current) {
      iframeRef.current.src = src;
      setActiveSrc(iframeRef.current.src);
    } else {
      setActiveSrc(src);
    }
  };

  const refreshActiveSrc = (callback?: (a: Window) => void) => {
    try {
      if (iframeRef.current) {
        const { contentWindow } = iframeRef.current;
        if (contentWindow) {
          setActiveSrc(contentWindow.location.href);
          if (callback) callback(contentWindow);
        }
      }
    }
    catch (e) {
      // qos 0 - call and forget function
      // do nothing on error
    }
  };

  const handleIframeLoad = () => {
    refreshActiveSrc((contentWindow) => {
      contentWindow.onunload = () => setTimeout(() => {
        refreshActiveSrc();
      });
    });
  };

  useEffect(() => {
    (async () => await loadDirectory())();

    // due to browsers critical history security, it's impossible to track/manipulate the history in iframe
    // the code below is a trick that will erase the history of iframe when file explorer is closed
    const initLength = window.history.length;
    return () => {
      window.history.pushState({purpose: 're-track history length'}, '', `${window.location.hash}?push-history=${new Date().getTime()}` );
      window.history.go(initLength - window.history.length);

      setTimeout(() => {
        window.history.pushState({purpose: 'overwrite forward items'}, '', `${window.location.hash}?reset-history=${new Date().getTime()}`);
        window.history.go(-1);
      });
    };
  }, []);

  if (isLoading)
    return (
      <div className="w-100 h-100 d-flex align-items-center justify-content-center">
        <div className="text-center p-5">
          <CircularProgress />
          <p>Loading Directory</p>
        </div>
      </div>
    );

  if (errorMessage)
    return (
      <div className="w-100 h-100 d-flex align-items-center justify-content-center">
        <div className="text-center p-5">
          <h4>Unable to load directory</h4>
          <p className="mb-3">{errorMessage}</p>
          <Button
            type="button"
            variant="outlined"
            size="small"
            color="primary"
            onClick={() => loadDirectory()}>
            Try Again
          </Button>
        </div>
      </div>
    );

  return (
    <SplitPane
      className="w-100 h-100"
      split="vertical"
      minSize={300}
      maxSize={500}
      defaultSize={300}
    >
      <div className={classes.panel}>
        <div className="p-3">
          <h5>Zip File Explorer</h5>
          <p className="m-0">File ID: {fileId}</p>
          <Divider className="my-2" />
          <Grid container justify="space-between" alignItems="center">
            <Grid item>
              <p className="m-0">View Source</p>
            </Grid>
            <Grid item>
              <Switch
                name="View Source"
                color="primary"
                checked={showTextOnly}
                onChange={e => setShowTextOnly(e.target.checked)}
              />
            </Grid>
          </Grid>
          <Divider className="mt-2 mb-3" />
          <FileExplorer
            activeSrc={activeSrc}
            rootUrl={`${directoryRootUrl}/`}
            onSelect={handleSelect} directory={directory} />
        </div>
      </div>

      <div className="w-100 h-100 position-absolute">
        {(() => {
          if (showTextOnly)
            return (
              <CodeViewer src={activeSrc} />
            );

          return (
            <iframe
              title="preview"
              src={activeSrc}
              ref={iframeRef}
              onLoad={handleIframeLoad}
              className={classes.iframe} />
          );
        })()}
      </div>
    </SplitPane>
  );
};
